import { useMsal } from '@azure/msal-react';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Input, InputOnChangeData, Label, Link, Switch, SwitchOnChangeData, Textarea, TextareaOnChangeData, Toast, ToastTitle, ToastTrigger, useToastController } from '@fluentui/react-components';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Consultant, ConsultantDocument, ConsultantDocumentPatch } from '../../interfaces/Consultant';
import { ApplicationState } from '../../store';
import * as TeamsState from '../../store/TeamsState';
import { ConsultantActionCreators } from '../../store/consultants';
import { useEditorStyles, useStackClassName } from '../Styles/Editor';
import { TokenHelper } from '../../TokenHelper';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { UserLoginState } from '../../store/Login';
import { onFormatDate } from '../Tools/converters';
import { ConvertionHelpers } from '../../ConvertionHelpers';
import { DocumentTypeDropdown } from '../EditorHelpers/DocumentTypeDropDown';
import { useMemo } from 'react';
import { FileViewer } from '../FileViewer/FileViewer';
import { CurrencyInput } from '../EditorHelpers/CurrencyInput';
import { DownloadFile } from '../Tools/FileDownloadHelper';
export const ConsultantsDocumentsEditor = () => {
    // All data needed for edition should be loaded before calling the editor
    const UserLoginState = useSelector<ApplicationState, UserLoginState>((state: ApplicationState) => state.userNeomyticLogin);
    const teamsState = useSelector<ApplicationState, TeamsState.TeamsState>((state: ApplicationState) => state.teamsState);
    const consultantDocumentEdit = useSelector<ApplicationState, ConsultantDocument | null>((state: ApplicationState) => state.consultantsState.consultantDocumentEdit);
    const consultants = useSelector<ApplicationState, Consultant[] | undefined>((state: ApplicationState) => state.consultantsState.consultants);
    // Gen hooks
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const { t } = useTranslation();
    const styles = useEditorStyles();
    const { dispatchToast, updateToast } = useToastController("NeoToasterUnique");
    // Fields
    const consultant = useMemo(() => {
        if (consultants && consultantDocumentEdit) {
            return consultants.find((c) => c.consultantid === consultantDocumentEdit.consultantid);
        }
        return undefined;
    }, [consultants, consultantDocumentEdit]);

    const [file, setFile] = React.useState<Blob>();
    const [documentType, setDocumentType] = React.useState<number>(consultantDocumentEdit ? consultantDocumentEdit.documentTypeId : 1);
    const [documentSource, setDocumentSource] = React.useState<string>(consultantDocumentEdit && consultantDocumentEdit.documentSource ? consultantDocumentEdit.documentSource : "unknown");
    const [documentDate, setDocumentDate] = React.useState<Date>(consultantDocumentEdit && consultantDocumentEdit.documentDate ? new Date(consultantDocumentEdit.documentDate) : new Date());
    const [documentAmount, setDocumentAmount] = React.useState<number | null>(consultantDocumentEdit && consultantDocumentEdit.documentAmount ? consultantDocumentEdit.documentAmount : null);
    const [description, setDescription] = React.useState<string | null>(consultantDocumentEdit ? consultantDocumentEdit.description : "");
    const [accountingRef, setAccountingRef] = React.useState<string | null>(consultantDocumentEdit ? consultantDocumentEdit.accountingRef : "");
    const [lock, setLock] = React.useState<boolean>(consultantDocumentEdit ? consultantDocumentEdit.lock : false);
    // Form validation
    const [isSubmitValid, setSubmitValid] = React.useState(false);

    React.useEffect(() => {
        if (consultantDocumentEdit)
            TokenHelper.GetToken(instance, teamsState)
                .then(token => {
                    if (token !== null && consultantDocumentEdit) {
                        DownloadFile('api/ConsultantsDocuments/Document/' + consultantDocumentEdit.consultantdocumentid, token)
                            .then((f) => {
                                if (f) {
                                    setFile(f);
                                }
                            })
                            .catch((e) => { console.log(e); });
                    }
                });
    }, []);

    React.useEffect(() => {
        setSubmitValid(documentType !== null
            && documentSource !== null && documentSource.length > 0
            && documentDate !== null
        );
    }, [documentType, documentSource, documentDate, documentAmount, description, accountingRef, lock]);

    const submit = () => {
        if (consultantDocumentEdit) {

            const c: ConsultantDocumentPatch = {
                consultantdocumentid: consultantDocumentEdit.consultantdocumentid,
                documentTypeId: documentType,
                documentDate: ConvertionHelpers.DateToISODateOnly(documentDate) ?? "",
                documentSource: documentSource,
                documentAmount: documentAmount,
                lock: lock,
                accountingRef: accountingRef,
                description: description,
            };

            TokenHelper.GetToken(instance, teamsState)
                .then(token => {
                    if (token !== null) {
                        const toastId = (Math.random() + 1).toString(36).substring(7);
                        dispatchToast(
                            <Toast appearance="inverted">
                                <ToastTitle>
                                    Saving...
                                </ToastTitle>
                            </Toast>,
                            { intent: "info", timeout: -1, position: "top-end", toastId: toastId }
                        );
                        fetch("api/ConsultantsDocuments/" + consultantDocumentEdit.consultantdocumentid, {
                            method: "PUT",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: "bearer " + token,
                            },
                            body: JSON.stringify(c),
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 200) {
                                    updateToast({
                                        content: (
                                            <Toast appearance="inverted">
                                                <ToastTitle action={
                                                    <ToastTrigger>
                                                        <Link>Dismiss</Link>
                                                    </ToastTrigger>
                                                }>
                                                    Saved
                                                </ToastTitle>
                                            </Toast>
                                        ),
                                        intent: "success",
                                        toastId,
                                        timeout: 5000,
                                    });
                                    return response.json() as Promise<ConsultantDocument>;
                                }
                                else {
                                    updateToast({
                                        content: (
                                            <Toast appearance="inverted">
                                                <ToastTitle action={
                                                    <ToastTrigger>
                                                        <Link>Dismiss</Link>
                                                    </ToastTrigger>
                                                }>
                                                    Save failed
                                                </ToastTitle>
                                            </Toast>
                                        ),
                                        intent: "error",
                                        toastId,
                                        timeout: 10000,
                                    });
                                }
                            })
                            .then(data => {
                                if (data) {
                                    dispatch(ConsultantActionCreators.patchConsultantDocumentEdition(data));
                                    dispatch(ConsultantActionCreators.requestConsultantDocumentEdition(null));
                                }
                            });
                    }
                });
        }
    };

    return <Dialog
        open
        modalType="modal">
        <DialogSurface className={styles.DialogContentWithFilePreview}>
            <DialogBody className={styles.fluid}>
                <DialogTitle>{t('EditDocument', { consultant: consultant ? (consultant.companyName ?? consultant.displayName) : "?", interpolation: { escapeValue: false } })}</DialogTitle>
                <DialogContent>
                    <div className={styles.onSingleLinWithFilePreviewe}>
                        <FileViewer file={file} />
                        <div className={styles.InfoPart}>
                            <form
                                onSubmit={() => {
                                    dispatch(ConsultantActionCreators.requestConsultantDocumentEdition(null));
                                }}
                                className={useStackClassName()}
                                autoComplete="off"
                            >
                                {(UserLoginState.rigths && UserLoginState.rigths.isAdmin) &&
                                    <div className={styles.onSingleLine}>
                                        <div className={styles.field50}>
                                            <Field label={t('Consultant')} className={styles.fluid}>
                                                <Label>{consultant ? consultant.displayName : "-"}</Label>
                                            </Field>
                                        </div>
                                        <div className={styles.field50}>
                                            <Field label={t('DateOfSubmission')} className={styles.fluid}>
                                                <Label>{consultantDocumentEdit && consultantDocumentEdit.createDate ? new Date(consultantDocumentEdit.createDate + "Z").toLocaleString() : "-"}</Label>
                                            </Field>
                                        </div>
                                    </div>
                                }
                                <Field label="Type" required>
                                    <DocumentTypeDropdown
                                        currentDocumentType={documentType}
                                        setDocumentType={setDocumentType}
                                    />
                                </Field>
                                <Field label="Source" required>
                                    <Input
                                        autoComplete="off"
                                        className={styles.fluid}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setDocumentSource(data.value)}
                                        value={documentSource !== null ? documentSource : ""}
                                        aria-invalid={documentSource === null}
                                        maxLength={50}
                                    />
                                </Field>
                                <Field label="Date"
                                    className={styles.fluid}
                                    required
                                >
                                    <DatePicker className={styles.fluid}
                                        onSelectDate={(date: Date | null | undefined) => { setDocumentDate(date === undefined || date === null ? new Date() : date); }}
                                        value={documentDate}
                                        formatDate={onFormatDate}
                                        placeholder={t('SelectDocumentDate')}
                                        aria-invalid={documentDate === null}
                                        minDate={new Date(2024, 0, 1)}
                                        maxDate={new Date()}
                                    />
                                </Field>
                                <Field label={t('Amount') + " (" + t('IncludVAT') + ")"}
                                    className={styles.fluid}
                                >
                                    <CurrencyInput
                                        setCurrencyOrNull={setDocumentAmount}
                                        currency={documentAmount}
                                        digits={2}
                                    />
                                </Field>
                                <Field label="Description">
                                    <Textarea
                                        className={styles.fluid}
                                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>, data: TextareaOnChangeData) => setDescription(data.value)}
                                        value={description !== null ? description : ""}
                                        resize="vertical"
                                    />
                                </Field>
                                {UserLoginState && UserLoginState.rigths && (UserLoginState.rigths.isAdmin || UserLoginState.rigths.canManageConsultantDocuments) && <>
                                    <Field label={t('AccountingRef')} >
                                        <Input
                                            autoComplete="off"
                                            className={styles.fluid}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setAccountingRef(data.value)}
                                            value={accountingRef !== null ? accountingRef : ""}
                                            maxLength={50}
                                        />
                                    </Field>
                                    <Switch label="is Locked"
                                        checked={lock}
                                        onChange={(ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => { setLock(data.checked); }} />
                                </>}
                            </form>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => dispatch(ConsultantActionCreators.requestConsultantDocumentEdition(null))}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        onClick={() => submit()}
                        type="submit"
                        form="my-form-id"
                        appearance="primary"
                        disabled={!isSubmitValid}
                    >
                        {t("Save")}
                    </Button>
                </DialogActions>
            </DialogBody>
        </DialogSurface>
    </Dialog>;
};